/**
 * Client Scenarios Section Component
 * Shows different use cases and who OKR Hub is for
 */

import React from 'react';
import { Section } from './Section';
import { SectionHeader } from './SectionHeader';
import { Quote, CheckCircle, Rocket, Briefcase, RefreshCw } from 'lucide-react';

interface Scenario {
  icon: React.ReactNode;
  title: string;
  quote: string;
  impact: string[];
  color: 'primary' | 'success' | 'warning';
}

const scenarios: Scenario[] = [
  {
    icon: <Rocket className="w-8 h-8" />,
    title: 'Founder-Led Growing Company',
    quote: 'We are growing fast, but priorities keep shifting.',
    impact: [
      'Clear company-level objectives',
      'Defined leadership ownership',
      'Aligned team OKRs',
      'Predictable execution rhythm'
    ],
    color: 'primary'
  },
  {
    icon: <Briefcase className="w-8 h-8" />,
    title: 'Professional Services Firm',
    quote: 'Everyone is busy, but accountability is unclear.',
    impact: [
      'Outcome-based objectives',
      'Transparent ownership',
      'Review cadence that drives learning',
      'Integration with existing KPIs'
    ],
    color: 'success'
  },
  {
    icon: <RefreshCw className="w-8 h-8" />,
    title: 'First-Time OKR Adoption',
    quote: 'We tried OKRs once. It didn\'t stick.',
    impact: [
      'Reframed OKRs as a management system',
      'Leader coaching during cycles',
      'External rigor through Managed Services',
      'Cultural adoption, not forced compliance'
    ],
    color: 'warning'
  }
];

const getColorClasses = (color: 'primary' | 'success' | 'warning') => {
  const colorMap = {
    primary: {
      bg: 'bg-primary-50',
      border: 'border-primary-300',
      icon: 'bg-primary-500 text-white',
      text: 'text-primary-600'
    },
    success: {
      bg: 'bg-success-50',
      border: 'border-success-300',
      icon: 'bg-success-500 text-white',
      text: 'text-success-600'
    },
    warning: {
      bg: 'bg-warning-50',
      border: 'border-warning-300',
      icon: 'bg-warning-500 text-white',
      text: 'text-warning-600'
    }
  };
  return colorMap[color];
};

export const ClientScenariosSection: React.FC = () => {
  return (
    <Section id="scenarios" background="white" padding="xl">
      <SectionHeader
        title="Who It's For"
        description="Real scenarios from real organizations. See where you fit."
        align="center"
      />

      <div className="grid lg:grid-cols-3 gap-8">
        {scenarios.map((scenario, index) => {
          const colors = getColorClasses(scenario.color);
          return (
            <div
              key={index}
              className={`${colors.bg} border-2 ${colors.border} rounded-2xl p-8 hover:shadow-soft-lg transition-all duration-300`}
            >
              {/* Icon */}
              <div className={`w-16 h-16 ${colors.icon} rounded-xl flex items-center justify-center mb-6`}>
                {scenario.icon}
              </div>

              {/* Title */}
              <h3 className="font-display text-2xl text-ink mb-4">
                {scenario.title}
              </h3>

              {/* Quote */}
              <div className="relative mb-6">
                <Quote className={`w-10 h-10 ${colors.text} opacity-30 absolute -top-2 -left-2`} />
                <p className="text-xl text-ink font-medium italic pl-6 leading-relaxed">
                  "{scenario.quote}"
                </p>
              </div>

              {/* Impact */}
              <div className="space-y-2">
                <div className={`text-sm font-semibold uppercase tracking-wider ${colors.text} mb-3`}>
                  OKR Hub Impact
                </div>
                <ul className="space-y-3">
                  {scenario.impact.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle className={`w-4 h-4 ${colors.text}`} />
                      </div>
                      <span className="text-ink-light text-sm leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Context */}
      <div className="mt-16 max-w-3xl mx-auto text-center">
        <p className="text-xl text-ink-light leading-relaxed">
          Whether you're scaling fast, managing complexity, or recovering from a failed OKR attempt—
          <span className="text-primary-600 font-semibold"> OKR Hub meets you where you are</span> and helps you move forward with confidence.
        </p>
      </div>
    </Section>
  );
};
